import { oc } from "@orpc/contract";
import { z } from "zod";
import { InvoiceEventSchema } from "./ar-invoices.contract";
import { RemittanceLineItemKindSchema } from "./ar-remittances.contract";
import { TaskPrioritySchema, TaskStatusSchema } from "../tasks.contract";
import {
  listSortShape,
  PdfDownloadOutputSchema,
  tablePdfColumnsSchema,
} from "../../schemas/common";

/**
 * AR Collection Threads RPC Contract
 *
 * Tracks collections follow-up items (short pays, credit limit expiries, etc.)
 * with a per-thread activity feed of comments and system events.
 */

/**
 * Human-readable thread identifier, e.g. `COL-1024`. The numeric part is a
 * global auto-increment column (`referenceNumber`) — see the model. Keep the
 * prefix and (un)formatting here so the backend serializer, the quick-find
 * search, and the frontend all derive the code the same way.
 */
export const COLLECTION_THREAD_CODE_PREFIX = "COL-";

export function formatCollectionThreadCode(referenceNumber: number): string {
  return `${COLLECTION_THREAD_CODE_PREFIX}${referenceNumber}`;
}

/**
 * Pulls the numeric reference out of a user-typed code. Tolerates the prefix
 * in any case ("col-12", "COL-12") and a bare number ("12"). Returns null when
 * no positive integer is present so callers can fall back to title matching.
 */
export function parseCollectionThreadCode(input: string): number | null {
  const digits = input.replace(/[^0-9]/g, "");
  if (digits.length === 0) return null;
  const parsed = Number.parseInt(digits, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export const CollectionThreadTypeSchema = z.enum([
  "short_pay",
  "over_payment",
  "deduction",
  "other",
]);

export const CollectionThreadStatusSchema = z.enum(["open", "resolved"]);

export const CollectionThreadResolutionSchema = z.enum([
  "paid_in_full",
  "short_pay_accepted",
  "written_off",
  "customer_no_response",
  "no_longer_relevant",
  "opened_by_mistake",
  // Over-payment outcomes — the residual is money owed back to the customer.
  "refunded",
  "applied_to_account",
  "credit_issued",
  // Deduction outcomes — set after an AR rep validates the claimed deduction.
  "deduction_approved",
  "deduction_charged_back",
  "other",
]);

export const CollectionThreadActivityKindSchema = z.enum([
  "comment",
  /**
   * A rep's recorded next step: waiting-on reason + follow-up date, with the
   * note in `body` and `{ waitingOnKey, waitingOnLabel, followUpAt }` in
   * `payload` (label snapshotted for the timeline; the thread itself stores
   * only the key). Distinct from `comment` so it skips the
   * CollectionThreadCommented notification fan-out.
   */
  "next_step",
  "status_change",
  "assignment_change",
  "system_event",
  // Ingest-driven bookkeeping (e.g. invoice amounts refreshed from Famous).
  // Rendered on the timeline like a system_event, but EXCLUDED from the
  // staleness clock (MAX(activity.created_at) drives urgency ranking and the
  // reminder workflow) — background writes must not make a thread look
  // freshly worked.
  "background_event",
]);

export const CollectionThreadActivitySchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  kind: CollectionThreadActivityKindSchema,
  body: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: z.string().uuid().nullable(),
  /**
   * Display name of the user who left the activity. Null when the activity
   * was system-generated (no actor) or the user record is unavailable.
   */
  actorName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Structured "likely cause" blob stamped at `metadata.remediation` when the
 * open-time discrepancy interpreter matched the detection against one of the
 * org's remediation classes using the customer's recorded notes. Parse thread
 * metadata with `.safeParse` — threads opened before interpretation existed
 * (or without a match) simply lack the key.
 */
export const CollectionThreadRemediationMetadataSchema = z.object({
  /** Remediation class key from the org taxonomy, e.g. "lumper_fee". */
  key: z.string(),
  /** Class label at interpretation time, e.g. "Lumper fee adjustment". */
  label: z.string(),
  confidence: z.enum(["high", "medium"]),
  /** One-sentence grounding in the customer note that drove the match. */
  explanation: z.string(),
  source: z.literal("customer_notes"),
});
export type CollectionThreadRemediationMetadata = z.infer<
  typeof CollectionThreadRemediationMetadataSchema
>;

export const CollectionThreadSchema = z.object({
  id: z.string().uuid(),
  /** Global auto-increment sequence behind the readable code. */
  referenceNumber: z.number().int(),
  /** Human-readable identifier derived from `referenceNumber`, e.g. `COL-1024`. */
  referenceCode: z.string(),
  customerId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable(),
  type: CollectionThreadTypeSchema,
  status: CollectionThreadStatusSchema,
  resolution: CollectionThreadResolutionSchema.nullable(),
  title: z.string(),
  description: z.string().nullable(),
  assigneeUserId: z.string().uuid().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  resolvedByUserId: z.string().uuid().nullable(),
  resolvedAt: z.string().nullable(),
  /**
   * Snooze gate. When set to a future timestamp the thread is suppressed from
   * reminders and sinks to the bottom of the urgency ranking until it elapses.
   * Null means not snoozed. Also bumped by the reminder workflow's auto-cooldown.
   */
  remindAfter: z.string().nullable(),
  /**
   * Why the thread is parked — the frozen key of an
   * `ar.collections.waitingOnOptions` entry. Resolve the label from the org's
   * module config at render (options are disabled, never deleted, so old keys
   * stay resolvable). Null = not parked / never triaged.
   */
  waitingOnKey: z.string().nullable(),
  /**
   * The rep's "come back to me on" date. Deliberately separate from
   * `remindAfter`, which doubles as the reminder-workflow cooldown gate.
   * Null or past-due qualifies the thread for the "Needs me now" view.
   */
  followUpAt: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CollectionThreadWithActivitiesSchema =
  CollectionThreadSchema.extend({
    activities: z.array(CollectionThreadActivitySchema),
    /**
     * Lifecycle events (`ar_invoice_events`) for the thread's linked invoice
     * — invoice opened/sent, remittance received, payment received. Empty
     * when the thread has no linked invoice. Only populated by `get`; other
     * mutating procedures that share this schema (create/update/resolve/
     * reopen/addComment) return an empty array to avoid a redundant fetch.
     */
    invoiceEvents: z.array(InvoiceEventSchema),
  });

/**
 * Sortable columns for the cross-customer threads table. Each maps to a
 * concrete order clause in the service — `customerName` orders by the joined
 * customer record. Mirrors the `sortBy`/`sortDirection` convention used by the
 * AR invoices and payments list tables.
 */
export const CollectionThreadSortBySchema = z.enum([
  "referenceNumber",
  "customerName",
  "title",
  "type",
  "status",
  "createdAt",
  "resolvedAt",
  /**
   * Most recent activity of any kind (agent writes included), falling back to
   * the thread's own `createdAt` when it has none yet. This is the "what moved
   * last" ordering reps need to resume a session — `createdAt` never reorders,
   * so a queue sorted by it can't drain.
   */
  "lastActivity",
  /** The assignee's name, unassigned threads last. Keyed by the column id. */
  "assigneeUserId",
  /** The follow-up date, threads without one last. */
  "followUpAt",
  /** The resolution, open (unresolved) threads last. */
  "resolution",
]);

/**
 * The list's attention lens, evaluated server-side so it can drive both the
 * page and the segmented-control counts:
 *
 * - `needs_me`: open threads assigned to the viewer OR unassigned, where the
 *   park expired — no follow-up recorded (never triaged), follow-up due, or
 *   activity newer than the viewer's last authored activity ("the world
 *   moved"). With zero next-step adoption this degrades to exactly the plain
 *   open list, never worse than the status quo.
 * - `waiting`: the complement — open, correctly parked, nothing new.
 * - `all`: no attention predicate (plain filters only).
 *
 * The predicate never references specific waiting-on keys, so an org renaming
 * or replacing its whole option set can't break the view.
 */
export const CollectionThreadAttentionSchema = z.enum([
  "needs_me",
  "waiting",
  "all",
]);

/**
 * Sentinel accepted alongside user ids in the list's `assignees` filter,
 * selecting threads with no assignee at all. Shared FE/BE so the dropdown and
 * the where-clause can't drift.
 */
export const UNASSIGNED_ASSIGNEE_FILTER = "unassigned";

/**
 * One entry of the `assignees` filter: a user id or the unassigned sentinel.
 * Exported so URL-state validators (routes.tsx) reject the same values the
 * API would.
 */
export const CollectionThreadAssigneeFilterSchema = z.union([
  z.string().uuid(),
  z.literal(UNASSIGNED_ASSIGNEE_FILTER),
]);

export const ListCollectionThreadsInputSchema = z.object({
  customerId: z.string().uuid().optional(),
  invoiceId: z.string().uuid().optional(),
  /** Single-assignee filter kept for non-table callers; the table's
   * "Assigned to" dropdown sends `assignees` instead. */
  assigneeUserId: z.string().uuid().optional(),
  /**
   * Multi-assignee filter: user ids, plus the `unassigned` sentinel for
   * threads nobody owns. Entries are OR'd together.
   */
  assignees: z.array(CollectionThreadAssigneeFilterSchema).optional(),
  status: z.array(CollectionThreadStatusSchema).optional(),
  type: z.array(CollectionThreadTypeSchema).optional(),
  resolution: z.array(CollectionThreadResolutionSchema).optional(),
  /**
   * Filter on the parked reason. Plain strings, not a closed enum — the option
   * set is org-defined config.
   */
  waitingOn: z.array(z.string().min(1).max(64)).optional(),
  attention: CollectionThreadAttentionSchema.optional(),
  /** Free-text match on title, the `COL-` code, or customer name. */
  search: z.string().optional(),
  ...listSortShape(CollectionThreadSortBySchema),
  limit: z.number().int().min(1).max(200).optional(),
  offset: z.number().int().min(0).optional(),
});

/**
 * The most recent *human* note on a thread — a `comment` or a `next_step`, not
 * the whole activity stream. Agent writes (`email_sent`,
 * `evidence_auto_matched`, `portal_action_auto_dispatched`, …) share the
 * activity table, so including them would make every thread the agent touched
 * overnight read as freshly worked by a person.
 */
export const CollectionThreadLastNoteSchema = z.object({
  body: z.string().nullable(),
  actorUserId: z.string().uuid().nullable(),
  /** Null for system/agent-authored rows, matching the activity DTO. */
  actorName: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * A thread enriched with its customer name and the per-viewer attention signals
 * the list uses to answer "what was I doing, and what changed since?"
 *
 * `myLastTouchAt` is derived from activity rows this viewer authored, so it
 * tracks decisions rather than eyeballs — a rep who only reads a thread leaves
 * no trace, which is deliberate (opening is not processing).
 */
export const CollectionThreadListItemSchema = CollectionThreadSchema.extend({
  customerName: z.string().nullable(),
  lastNote: CollectionThreadLastNoteSchema.nullable(),
  /** Most recent activity of any kind; null when the thread has none. */
  lastActivityAt: z.string().nullable(),
  /** Most recent activity authored by the requesting user; null if never. */
  myLastTouchAt: z.string().nullable(),
});

export const ListCollectionThreadsOutputSchema = z.object({
  threads: z.array(CollectionThreadListItemSchema),
  total: z.number().int(),
  /**
   * Open-thread counts for the attention segmented control, computed over the
   * request's non-attention filters so the badges always describe what each
   * segment would show. Only returned when the request carried an `attention`
   * param (the plain list — inbox rail, per-customer panel — skips the two
   * extra count queries).
   */
  attentionCounts: z
    .object({
      needsMe: z.number().int(),
      waiting: z.number().int(),
    })
    .optional(),
});

/**
 * A thread enriched with the signals used to rank "needs attention":
 * - `dollarsAtRisk`: linked invoice balance, falling back to the customer's
 *   overdue balance when no invoice is linked (decimal string).
 * - `daysSinceLastActivity`: whole days since the most recent activity row;
 *   null when the thread has no activity yet.
 * - `urgencyScore`: composite of type severity + dollars + staleness. Derived
 *   at query time; never persisted.
 */
export const RankedCollectionThreadSchema = CollectionThreadSchema.extend({
  customerName: z.string().nullable(),
  dollarsAtRisk: z.string(),
  daysSinceLastActivity: z.number().nullable(),
  urgencyScore: z.number(),
});

export const ListRankedCollectionThreadsInputSchema = z.object({
  limit: z.number().int().min(1).max(200).optional(),
  type: z.array(CollectionThreadTypeSchema).optional(),
  assigneeUserId: z.string().uuid().optional(),
});

export const ListRankedCollectionThreadsOutputSchema = z.object({
  threads: z.array(RankedCollectionThreadSchema),
});

export const GetCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * An outstanding AR task that points a user back at this thread — what drives
 * the "the system needs you here" banner pinned to the top of the thread.
 *
 * Tasks link to threads through `metadata.arAction.threadId` (see
 * `arTaskActionSchema` in `@erp/shared-types`); there is no FK. `metadata` is
 * returned verbatim so the banner can run the same `parseArTaskAction` the
 * inbox and dashboard use and reuse `ArTaskActionButton` for the
 * complete/act affordance — the plain-language "why" is derived from that
 * parsed payload client-side, where it can be translated.
 */
export const CollectionThreadReminderTaskSchema = z.object({
  id: z.string().uuid(),
  /** `AR-12`-style code; null when the task has no namespace number. */
  displayId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  assigneeUserId: z.string().uuid().nullable(),
  /** Resolved server-side so the banner needs no second users query. */
  assigneeName: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  /** When the system filed the reminder. */
  createdAt: z.string(),
});

export const GetCollectionThreadReminderTaskInputSchema = z.object({
  id: z.string().uuid(),
});

export const GetCollectionThreadReminderTaskOutputSchema = z.object({
  /** Null when nothing is outstanding — the banner renders nothing. */
  task: CollectionThreadReminderTaskSchema.nullable(),
});

/**
 * One row per customer with open collection threads — powers the Collections
 * inbox left rail and the AR Summary mini "customers needing attention" card.
 *
 * - `openCount`: number of OPEN threads on this customer (resolved threads
 *   excluded so the rail reflects the active queue).
 * - `totalAtRisk`: sum of linked invoice balances for the open threads
 *   (decimal string). Threads without a linked invoice contribute 0.
 * - `oldestOpenedAt`: timestamp of the oldest still-open thread on this
 *   customer; primary sort key so reps see the staleness leader first.
 * - `mostRecentActivityAt`: latest `updatedAt` across the customer's open
 *   threads; surfaces "something happened recently" without a separate query.
 */
export const InboxCustomerSummarySchema = z.object({
  customerId: z.string().uuid(),
  customerName: z.string().nullable(),
  openCount: z.number().int(),
  totalAtRisk: z.string(),
  oldestOpenedAt: z.string(),
  mostRecentActivityAt: z.string(),
});

export const ListInboxCustomersInputSchema = z.object({
  limit: z.number().int().min(1).max(500).optional(),
});

export const ListInboxCustomersOutputSchema = z.object({
  customers: z.array(InboxCustomerSummarySchema),
});

export const CreateCollectionThreadInputSchema = z.object({
  customerId: z.string().uuid(),
  invoiceId: z.string().uuid().optional(),
  type: CollectionThreadTypeSchema,
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  assigneeUserId: z.string().uuid().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const UpdateCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  assigneeUserId: z.string().uuid().nullable().optional(),
  // Pass null to clear the linked invoice. Setting a different invoice
  // re-derives evidence on the backend (old evidence rows are dropped).
  invoiceId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const ResolveCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  resolution: CollectionThreadResolutionSchema,
  comment: z.string().optional(),
});

/**
 * Resolve many threads at once from the list view. One `resolution` (and an
 * optional shared note) is applied to every selected thread. Capped at 200 to
 * match the list's max page size, so a request never exceeds one full page.
 */
export const BulkResolveCollectionThreadsInputSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
  resolution: CollectionThreadResolutionSchema,
  comment: z.string().optional(),
});

export const BulkResolveCollectionThreadsOutputSchema = z.object({
  /** Threads that transitioned open → resolved on this call. */
  resolved: z.number().int(),
  /** Ids skipped because they were already resolved or no longer exist. */
  skipped: z.number().int(),
});

/**
 * Assign many threads to one user from the list view. The target user becomes
 * each thread's assignee — their `assigned` watch is created or promoted and
 * any other assigned watchers are demoted to plain watchers. `assigneeUserId:
 * null` unassigns instead. Capped at 200 to match the list's max page size.
 */
export const BulkAssignCollectionThreadsInputSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
  /** Target user; null clears the assignee(s). */
  assigneeUserId: z.string().uuid().nullable(),
});

export const BulkAssignCollectionThreadsOutputSchema = z.object({
  /** Threads whose assignee actually changed on this call. */
  assigned: z.number().int(),
  /**
   * Ids skipped: already assigned to this user, not open, or no longer
   * existing. A stale selection degrades quietly instead of erroring.
   */
  skipped: z.number().int(),
});

/** Column ids the threads PDF can render — the table's data columns. */
export const CollectionThreadPdfColumnIdSchema = z.enum([
  "referenceNumber",
  "customerName",
  "title",
  "lastActivity",
  "type",
  "assigneeUserId",
  "status",
  "waitingOnKey",
  "followUpAt",
  "resolution",
  "createdAt",
  "resolvedAt",
]);

/**
 * Download the selected threads as a letterhead PDF. `columns` mirrors the
 * caller's visible table layout — id order, display labels, and relative
 * widths — so the document matches the table the user configured on screen.
 * Row order follows `ids`. Capped at 200 to match the list's max page size.
 */
export const DownloadCollectionThreadsPdfInputSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
  columns: tablePdfColumnsSchema(CollectionThreadPdfColumnIdSchema),
});

export const DownloadCollectionThreadsPdfOutputSchema = PdfDownloadOutputSchema;

export const ReopenCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  comment: z.string().optional(),
});

/**
 * Snooze a thread by setting its reminder gate. `remindAfter` is an absolute
 * "snooze until" timestamp; while it is in the future the thread is excluded
 * from reminders and sinks below all active threads in the urgency ranking.
 * Pass `null` to clear the snooze immediately.
 */
export const SnoozeCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  remindAfter: z.string().datetime({ offset: true }).nullable(),
});

export const AddCollectionThreadCommentInputSchema = z.object({
  id: z.string().uuid(),
  body: z.string().min(1),
});

/**
 * Record the rep's next step on a thread in one interaction: why it's parked
 * (`waitingOnKey` from the org's configured options), when it should come back
 * (`followUpAt`), and a short note for the list + timeline. All three land
 * atomically — the thread columns update and a `next_step` activity is
 * written. Pass nulls to clear the park (back to "needs triage").
 *
 * Deliberately quiet: does NOT fan out watcher notifications — a rep parking
 * their own thread isn't news, and notifying on every pass trains people to
 * ignore the bell.
 */
export const SetCollectionThreadNextStepInputSchema = z.object({
  id: z.string().uuid(),
  waitingOnKey: z.string().min(1).max(64).nullable(),
  followUpAt: z.string().datetime({ offset: true }).nullable(),
  note: z.string().max(2000).optional(),
});

// --- Evidence DTOs ---
// Lightweight projections of related entities surfaced on the thread detail
// view. These mirror the relevant subset of fields from the underlying
// models — enough to render a card and link out, no more.

export const CollectionThreadEvidencePaymentApplicationSchema = z.object({
  id: z.string().uuid(),
  /** The cash receipt the application spends (GEN-3201). */
  bankTransactionId: z.string().uuid().nullable(),
  invoiceId: z.string().uuid(),
  appliedAmount: z.string(),
  applicationDate: z.string().nullable(),
  sequenceNumber: z.number().int().nullable(),
});

/** A cash receipt the invoice's applications spend (GEN-3202). */
export const CollectionThreadEvidenceReceiptSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  transactionDate: z.string().nullable(),
  amount: z.string(),
  /** How it was paid (check, eft, ACH, …). */
  paymentType: z.string().nullable(),
  referenceNumber: z.string().nullable(),
});

export const CollectionThreadEvidenceRemittanceAdviceSchema = z.object({
  id: z.string().uuid(),
  /** The cash receipt the remittance is actively linked to, if any. */
  bankTransactionId: z.string().uuid().nullable(),
  documentId: z.string().uuid().nullable(),
  inboundEmailId: z.string().uuid().nullable(),
  channel: z.string(),
  receivedAt: z.string(),
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: z.string().nullable(),
  statedPaymentDate: z.string().nullable(),
  statedPaymentMethod: z.string().nullable(),
  statedReferenceNo: z.string().nullable(),
});

export const CollectionThreadEvidenceRemittanceLineItemSchema = z.object({
  id: z.string().uuid(),
  remittanceAdviceId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable(),
  rawInvoiceRef: z.string(),
  statedAmount: z.string().nullable(),
  /**
   * Whether this line is a payment or an amount the customer withheld.
   *
   * Required to read `statedAmount` correctly: deduction lines store a POSITIVE
   * amount and carry the sign in this field instead, so a consumer that sums
   * `statedAmount` without checking `kind` adds withheld money to what the
   * customer said they paid.
   */
  kind: RemittanceLineItemKindSchema,
  matchStatus: z.string(),
  matchReason: z.string().nullable(),
});

export const CollectionThreadEvidenceInboundEmailSchema = z.object({
  id: z.string().uuid(),
  from: z.string().nullable(),
  to: z.string(),
  subject: z.string().nullable(),
  service: z.string(),
  receivedAt: z.string(),
  /** Classification types of the documents this email delivered (e.g. POD/BOL). */
  carriedDocumentClassifications: z.array(z.string()),
});

/**
 * An email sent from the thread's draft dialog — the sent-side counterpart of
 * the inbound-email evidence entries, rendered in the same "Emails" group.
 */
export const CollectionThreadEvidenceOutboundEmailSchema = z.object({
  id: z.string().uuid(),
  to: z.string(),
  subject: z.string(),
  sentAt: z.string(),
});

export const CollectionThreadEvidenceDocumentSchema = z.object({
  id: z.string().uuid(),
  originalFileName: z.string().nullable(),
  status: z.string(),
  classificationType: z.string().nullable(),
  storagePath: z.string().nullable(),
  /** Bytes on disk. Null when the upload pipeline never recorded a size. */
  fileSizeBytes: z.number().nullable(),
  createdAt: z.string(),
});

export const CollectionThreadEvidenceShipmentSchema = z.object({
  id: z.string().uuid(),
  salesOrderId: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  status: z.string(),
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  carrier: z.string().nullable(),
  trackingReference: z.string().nullable(),
});

export const CollectionThreadEvidenceInvoiceSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  invoiceNumber: z.string().nullable(),
  status: z.string(),
  invoiceDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  grossAmount: z.string().nullable(),
  balanceDue: z.string().nullable(),
  currencyCode: z.string().nullable(),
});

export const CollectionThreadEvidenceSalesOrderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  status: z.string(),
  orderDate: z.string().nullable(),
  totalAmount: z.string().nullable(),
  customerPurchaseOrderNumber: z.string().nullable(),
});

export const CollectionThreadEvidenceCustomerPurchaseOrderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  purchaseOrderNumber: z.string(),
  status: z.string(),
  orderDate: z.string().nullable(),
  totalAmount: z.string().nullable(),
});

export const CollectionThreadEvidenceApInvoiceSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  status: z.string(),
  paymentStatus: z.string(),
  invoiceDate: z.string().nullable(),
  totalAmount: z.string().nullable(),
  currency: z.string(),
  /**
   * True when a user linked this invoice via the manual "Link AP invoice"
   * action (vs. derived from supporting documents). The UI shows "Unlink" only
   * for these — document-derived invoices aren't directly removable.
   */
  manuallyLinked: z.boolean(),
});

export const ListCollectionThreadEvidenceInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * Files a previously-uploaded document as evidence on a thread. The document
 * must already be attached to the thread via the standard upload flow
 * (entityType `ar_collection_thread`); this links it into the thread's evidence
 * graph (COLLECTION_THREAD_EVIDENCE) and records a timeline activity.
 */
export const FileCollectionThreadDocumentEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  documentId: z.string().uuid(),
});

export const FileCollectionThreadDocumentEvidenceOutputSchema = z.object({
  /** False when the document was already filed on this thread (no-op). */
  filed: z.boolean(),
});

/**
 * Parses a previously-uploaded `.eml`/`.msg` file into a real InboundEmail and
 * files it as EMAIL evidence on a thread (so it renders in the email viewer and
 * feeds search + the collections agent, instead of sitting as an opaque blob).
 * Like `fileDocumentEvidence`, the document must already be attached to the
 * thread via the standard upload flow (entityType `ar_collection_thread`).
 * Rejects files whose content is not an email. Deduped org-wide by the email's
 * Message-ID: re-uploading an email Gently already knows just files the
 * existing row.
 */
export const FileCollectionThreadEmailFileEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  /** The uploaded `.eml`/`.msg` document, already attached to this thread. */
  documentId: z.string().uuid(),
});

export const FileCollectionThreadEmailFileEvidenceOutputSchema = z.object({
  /** False when the email was already filed on this thread (no-op). */
  filed: z.boolean(),
  /** The InboundEmail the file resolved to (freshly created or deduped). */
  inboundEmailId: z.string().uuid(),
  /** True when an existing email row was reused instead of creating a twin. */
  deduped: z.boolean(),
});

/**
 * Manually links an existing AP (vendor/carrier) invoice as evidence on a
 * thread. Links the invoice itself plus its supporting documents (BOL, POD,
 * carrier confirmation) into the evidence graph and records a timeline
 * activity. Use when the automatic gather missed it (e.g. a freight invoice
 * matched only by a shared reference number, with no AP_INVOICE_MATCHING row).
 */
export const LinkCollectionThreadApInvoiceEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  apInvoiceId: z.string().uuid(),
});

export const LinkCollectionThreadApInvoiceEvidenceOutputSchema = z.object({
  /** False when the invoice was already linked to this thread (no-op). */
  linked: z.boolean(),
  /** Supporting documents attached alongside the invoice. */
  linkedDocumentCount: z.number().int(),
});

/**
 * Removes a manually-linked AP invoice (and the supporting documents the manual
 * link brought along) from a thread's evidence. Only affects manually-filed
 * associations — gather-derived, email-filed, and upload-filed evidence is
 * never touched.
 */
export const UnlinkCollectionThreadApInvoiceEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  apInvoiceId: z.string().uuid(),
});

export const UnlinkCollectionThreadApInvoiceEvidenceOutputSchema = z.object({
  /** False when there was no manual link to remove (no-op). */
  unlinked: z.boolean(),
});

/**
 * Re-runs the evidence gather for a thread (the same walk that fires on thread
 * creation). Idempotent and re-runnable — picks up evidence that landed after
 * the thread was opened, including AP invoices newly matched by reference_ids.
 */
export const RefreshCollectionThreadEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
});

export const RefreshCollectionThreadEvidenceOutputSchema = z.object({
  /** Evidence rows newly linked on this run. */
  linked: z.number().int(),
  /** Rows skipped (already linked, or target since deleted). */
  skipped: z.number().int(),
  /** Rows that failed to link (unexpected errors). */
  failed: z.number().int(),
  /**
   * Historical inbound emails newly linked by the invoice-identifier search
   * (separate from `linked`, which counts the structured graph walk only).
   */
  emailsLinked: z.number().int(),
  /**
   * True when more emails matched than one run will attach. The refresh is
   * idempotent, so this is informational rather than a retry signal.
   */
  emailsTruncated: z.boolean(),
});

/**
 * Pulls the thread's linked invoice — and the Famous receipts that touched it —
 * straight from the ERP, then re-derives the thread's own state from the fresh
 * numbers. Fired once when a thread is opened so the collector reads today's
 * balance instead of last night's ingest.
 *
 * Best-effort by contract: a Famous/Oracle outage returns zeroes rather than an
 * error, because the already-ingested data behind it is still worth showing.
 */
export const RefreshCollectionThreadFromFamousInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
});

/**
 * Why a refresh did nothing. All four render identically (nothing at all), so
 * the reason has to be reported or a correctly-silent view is indistinguishable
 * from a broken one.
 *
 * - `no_linked_invoice` — customer-scoped thread; there is nothing to refresh.
 * - `famous_not_configured` — no `ORA_USER` / `ORA_PASSWORD` on this server.
 *   The usual answer on a local dev box, and the usual reason "every thread
 *   skips" rather than just repeats.
 * - `recently_refreshed` — the per-(org, thread, invoice) cooldown is still
 *   live from an open moments ago.
 * - `refresh_failed` — Oracle was reachable but the run threw; already logged
 *   and reported to Sentry.
 */
export const CollectionThreadRefreshSkipReasonSchema = z.enum([
  "no_linked_invoice",
  "famous_not_configured",
  "recently_refreshed",
  "refresh_failed",
]);

/**
 * Every field here means "this actually changed" — deliberately NOT the ingest
 * phases' upsert counts, which are non-zero on any re-ingest of unchanged data
 * and would have the UI announcing an update on nearly every thread open. The
 * refresh brackets itself with a before/after snapshot of the invoice.
 */
export const RefreshCollectionThreadFromFamousOutputSchema = z.object({
  /** The linked invoice's number, for user-facing copy. Null when unnumbered. */
  invoiceNumber: z.string().nullable(),
  /** Invoice balance before the refresh (decimal string). */
  balanceBefore: z.string().nullable(),
  /** Invoice balance after the refresh (decimal string). */
  balanceAfter: z.string().nullable(),
  /** True only when the balance moved numerically. */
  balanceChanged: z.boolean(),
  /**
   * Payment applications that did not exist on the invoice before this run —
   * genuinely new cash, not rows re-upserted by the ingest.
   */
  newPaymentsApplied: z.int().nonnegative(),
  /**
   * True when the refresh auto-resolved this thread — the invoice came back
   * paid in full. The client should re-read the thread to show the new state.
   */
  threadResolved: z.boolean(),
  /**
   * True when nothing ran: the thread has no linked invoice, Famous isn't
   * configured, the refresh failed, or an identical refresh ran moments ago.
   */
  skipped: z.boolean(),
  /**
   * Why nothing ran, or `null` when the refresh executed. Four unrelated
   * conditions all render the same (nothing), so without this a silent view is
   * indistinguishable from a broken one — "no Oracle credentials on this
   * server" and "Famous had no news" look identical to whoever is testing.
   */
  skipReason: CollectionThreadRefreshSkipReasonSchema.nullable(),
});

/**
 * Re-derive everything the thread page shows from today's source data: the
 * invoice (and its cash) from Famous, the evidence graph, and the AI recommended
 * action.
 *
 * A thread's three panels are each refreshable on their own, but the pieces are
 * a chain — Famous moves the invoice, the invoice moves the evidence, the
 * evidence moves the recommendation — so refreshing one in isolation leaves the
 * downstream ones quoting inputs that no longer exist. This runs the chain in
 * order, in one request, for the collector who just wants the page to be right.
 */
export const ReevaluateCollectionThreadInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
});

/**
 * Per-stage outcome. Each stage is independently fallible and independently
 * skippable, so the result reports them separately rather than collapsing to a
 * single boolean: "Famous is down" and "this thread has no AI recommendation"
 * are both partial successes, and the user is owed the difference.
 */
export const ReevaluateCollectionThreadOutputSchema = z.object({
  /** The Famous pull, verbatim from the shared refresh (including its skips). */
  famous: RefreshCollectionThreadFromFamousOutputSchema,
  /** Evidence rows newly linked by the structured walk. */
  evidenceLinked: z.int().nonnegative(),
  /** Historical inbound emails newly attached by the identifier search. */
  emailsLinked: z.int().nonnegative(),
  /**
   * True when the AI recommendation was regenerated. False for thread types
   * that have no recommendation pipeline (deduction, other) and when the agent
   * run failed — the rest of the re-evaluation still stands in both cases.
   */
  recommendationRegenerated: z.boolean(),
  /**
   * True when a stage threw and was absorbed. The re-evaluation is best-effort
   * per stage; this tells the UI to say "partially refreshed" rather than
   * claiming the page is now authoritative.
   */
  partial: z.boolean(),
});

export const ListCollectionThreadEvidenceOutputSchema = z.object({
  paymentApplications: z.array(
    CollectionThreadEvidencePaymentApplicationSchema,
  ),
  receipts: z.array(CollectionThreadEvidenceReceiptSchema),
  remittanceAdvices: z.array(CollectionThreadEvidenceRemittanceAdviceSchema),
  remittanceLineItems: z.array(
    CollectionThreadEvidenceRemittanceLineItemSchema,
  ),
  inboundEmails: z.array(CollectionThreadEvidenceInboundEmailSchema),
  outboundEmails: z.array(CollectionThreadEvidenceOutboundEmailSchema),
  documents: z.array(CollectionThreadEvidenceDocumentSchema),
  shipments: z.array(CollectionThreadEvidenceShipmentSchema),
  invoice: CollectionThreadEvidenceInvoiceSchema.nullable(),
  salesOrder: CollectionThreadEvidenceSalesOrderSchema.nullable(),
  customerPurchaseOrder:
    CollectionThreadEvidenceCustomerPurchaseOrderSchema.nullable(),
  apInvoices: z.array(CollectionThreadEvidenceApInvoiceSchema),
});

/**
 * Generates (or reads from cache) short, human-readable names for the thread's
 * evidence documents. Separate from listEvidence so the evidence list renders
 * immediately while names — which may require a cheap LLM call on a cold cache —
 * resolve in a second request.
 */
export const ListCollectionThreadDocumentNamesInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
});

export const ListCollectionThreadDocumentNamesOutputSchema = z.object({
  /** Only documents that resolved to a name; the UI falls back for the rest. */
  names: z.array(
    z.object({
      documentId: z.string().uuid(),
      displayName: z.string().min(1),
    }),
  ),
});

export const arCollectionThreadsContracts = {
  list: oc
    .input(ListCollectionThreadsInputSchema)
    .output(ListCollectionThreadsOutputSchema)
    .route({
      method: "GET",
      summary: "List collection threads",
      description:
        "Lists collection follow-ups (short pays, over-payments, deductions) filtered by customer, invoice, assignee, status, type, resolution, waiting-on reason or search, with last note and attention counts.",
    }),
  listRanked: oc
    .input(ListRankedCollectionThreadsInputSchema)
    .output(ListRankedCollectionThreadsOutputSchema)
    .route({
      method: "GET",
      summary: "List collection threads by urgency",
      description:
        "Returns collection threads ranked by urgency score combining type severity, dollars at risk and days since last activity, optionally filtered by type or assignee.",
    }),
  listInboxCustomers: oc
    .input(ListInboxCustomersInputSchema)
    .output(ListInboxCustomersOutputSchema)
    .route({
      method: "GET",
      summary: "List customers with open collection threads",
      description:
        "Returns one row per customer with open collection threads: open count, total dollars at risk, oldest open date and latest activity, oldest first.",
    }),
  get: oc
    .input(GetCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "GET",
      summary: "Get a collection thread",
      description:
        "Returns one collection thread by id with its activity timeline of comments, next steps, status changes and system events, plus linked invoice events.",
    }),
  /**
   * Sibling read to `get`: the outstanding reminder task for this thread, if
   * any. Deliberately NOT folded into `get`'s output — that schema is shared
   * with create/update/resolve/reopen/addComment, which would all have to
   * return a field they know nothing about, and the banner needs to refresh on
   * task mutations independently of the thread itself.
   */
  getReminderTask: oc
    .input(GetCollectionThreadReminderTaskInputSchema)
    .output(GetCollectionThreadReminderTaskOutputSchema)
    .route({
      method: "GET",
      summary: "Get a collection thread's open reminder task",
      description:
        "Returns the outstanding task pointing a user back at a collection thread (title, status, priority, assignee), or null when nothing is pending.",
    }),
  create: oc
    .input(CreateCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "POST",
      summary: "Create a collection thread",
      description:
        "Opens a collection follow-up for a customer and optional invoice; gathers evidence, generates an AI recommendation, runs opted-in portal actions and notifies watchers.",
    }),
  update: oc
    .input(UpdateCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "POST",
      summary: "Update a collection thread",
      description:
        "Edits a collection thread's title, description, assignee, metadata or linked invoice; changing the invoice re-gathers evidence and refreshes the AI recommendation.",
    }),
  resolve: oc
    .input(ResolveCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "POST",
      summary: "Resolve a collection thread",
      description:
        "Closes a collection thread with a resolution such as paid in full, short pay accepted, written off or credit issued, plus optional comment; notifies watchers.",
    }),
  bulkResolve: oc
    .input(BulkResolveCollectionThreadsInputSchema)
    .output(BulkResolveCollectionThreadsOutputSchema)
    .route({
      method: "POST",
      summary: "Resolve multiple collection threads",
      description:
        "Closes up to 200 open collection threads with one shared resolution and optional comment; returns resolved and skipped counts.",
    }),
  bulkAssign: oc
    .input(BulkAssignCollectionThreadsInputSchema)
    .output(BulkAssignCollectionThreadsOutputSchema)
    .route({
      method: "POST",
      summary: "Assign multiple collection threads",
      description:
        "Sets or clears the assignee on up to 200 open collection threads, demoting other assigned watchers; returns changed and skipped counts.",
    }),
  downloadPdf: oc
    .input(DownloadCollectionThreadsPdfInputSchema)
    .output(DownloadCollectionThreadsPdfOutputSchema)
    .route({
      method: "POST",
      summary: "Download collection threads as PDF",
      description:
        "Generates a letterhead PDF of up to 200 selected collection threads using the caller's column layout; returns base64 PDF bytes and filename.",
    }),
  reopen: oc
    .input(ReopenCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "POST",
      summary: "Reopen a collection thread",
      description:
        "Moves a resolved collection thread back to open with an optional comment recorded on its timeline.",
    }),
  snooze: oc
    .input(SnoozeCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "POST",
      summary: "Snooze a collection thread",
      description:
        "Suppresses reminders on a collection thread until a given time and drops it below active threads in urgency ranking; null clears the snooze.",
    }),
  setNextStep: oc
    .input(SetCollectionThreadNextStepInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({
      method: "POST",
      summary: "Set next step on a collection thread",
      description:
        "Records why a collection thread is waiting, its follow-up date and an optional note as a timeline entry, without notifying watchers; nulls clear it.",
    }),
  addComment: oc
    .input(AddCollectionThreadCommentInputSchema)
    .output(CollectionThreadActivitySchema)
    .route({
      method: "POST",
      summary: "Comment on a collection thread",
      description:
        "Adds a comment to a collection thread's timeline and notifies the thread's watchers; returns the new activity.",
    }),
  listEvidence: oc
    .input(ListCollectionThreadEvidenceInputSchema)
    .output(ListCollectionThreadEvidenceOutputSchema)
    .route({
      method: "GET",
      summary: "List evidence for a collection thread",
      description:
        "Returns everything linked to a collection thread: invoice, payments and applications, remittances, emails, documents, shipments, sales order, customer PO and AP invoices.",
    }),
  fileDocumentEvidence: oc
    .input(FileCollectionThreadDocumentEvidenceInputSchema)
    .output(FileCollectionThreadDocumentEvidenceOutputSchema)
    .route({
      method: "POST",
      summary: "File an uploaded document as thread evidence",
      description:
        "Links a document already uploaded to a collection thread into its evidence, records a timeline entry, and refreshes the AI recommendation.",
    }),
  fileEmailFileAsEvidence: oc
    .input(FileCollectionThreadEmailFileEvidenceInputSchema)
    .output(FileCollectionThreadEmailFileEvidenceOutputSchema)
    .route({
      method: "POST",
      summary: "File an uploaded email as thread evidence",
      description:
        "Parses an uploaded .eml or .msg file on a collection thread into an inbound email, files it as evidence (deduped by Message-ID) and refreshes the recommendation.",
    }),
  linkApInvoiceEvidence: oc
    .input(LinkCollectionThreadApInvoiceEvidenceInputSchema)
    .output(LinkCollectionThreadApInvoiceEvidenceOutputSchema)
    .route({
      method: "POST",
      summary: "Link an AP invoice to a collection thread",
      description:
        "Manually links a vendor or carrier AP invoice and its supporting documents (BOL, POD) as collection thread evidence and records a timeline entry.",
    }),
  unlinkApInvoiceEvidence: oc
    .input(UnlinkCollectionThreadApInvoiceEvidenceInputSchema)
    .output(UnlinkCollectionThreadApInvoiceEvidenceOutputSchema)
    .route({
      method: "POST",
      summary: "Unlink an AP invoice from a collection thread",
      description:
        "Removes a manually linked AP invoice and the supporting documents it brought from a collection thread's evidence; automatically gathered evidence is untouched.",
    }),
  refreshEvidence: oc
    .input(RefreshCollectionThreadEvidenceInputSchema)
    .output(RefreshCollectionThreadEvidenceOutputSchema)
    .route({
      method: "POST",
      summary: "Refresh evidence for a collection thread",
      description:
        "Re-runs the evidence gather for a collection thread, linking newly found payments, documents, AP invoices and historical emails; returns linked, skipped and failed counts.",
    }),
  refreshFromFamous: oc
    .input(RefreshCollectionThreadFromFamousInputSchema)
    .output(RefreshCollectionThreadFromFamousOutputSchema)
    .route({
      method: "POST",
      summary: "Refresh collection thread invoice from the ERP",
      description:
        "Pulls the thread's linked invoice and receipts from the ERP, updates balance and applied payments, and auto-resolves the thread if paid in full.",
    }),
  reevaluate: oc
    .input(ReevaluateCollectionThreadInputSchema)
    .output(ReevaluateCollectionThreadOutputSchema)
    .route({
      method: "POST",
      summary: "Re-evaluate a collection thread",
      description:
        "Refreshes the linked invoice and payments from the ERP, re-gathers evidence, then regenerates the AI recommendation in one request; reports per-stage outcomes.",
    }),
  documentNames: oc
    .input(ListCollectionThreadDocumentNamesInputSchema)
    .output(ListCollectionThreadDocumentNamesOutputSchema)
    .route({
      method: "GET",
      summary: "Get display names for thread documents",
      description:
        "Returns short readable names for a collection thread's evidence documents, generating them with AI when not already cached.",
    }),
} as const;

export type CollectionThreadType = z.infer<typeof CollectionThreadTypeSchema>;
export type CollectionThreadStatus = z.infer<
  typeof CollectionThreadStatusSchema
>;
export type CollectionThreadResolution = z.infer<
  typeof CollectionThreadResolutionSchema
>;
export type CollectionThreadActivityKind = z.infer<
  typeof CollectionThreadActivityKindSchema
>;
export type CollectionThreadDto = z.infer<typeof CollectionThreadSchema>;
export type CollectionThreadActivityDto = z.infer<
  typeof CollectionThreadActivitySchema
>;
export type CollectionThreadWithActivitiesDto = z.infer<
  typeof CollectionThreadWithActivitiesSchema
>;
export type CollectionThreadSortBy = z.infer<
  typeof CollectionThreadSortBySchema
>;
export type CollectionThreadAttention = z.infer<
  typeof CollectionThreadAttentionSchema
>;
export type CollectionThreadLastNoteDto = z.infer<
  typeof CollectionThreadLastNoteSchema
>;
export type CollectionThreadListItemDto = z.infer<
  typeof CollectionThreadListItemSchema
>;
export type ListCollectionThreadsInput = z.infer<
  typeof ListCollectionThreadsInputSchema
>;
export type ListCollectionThreadsOutput = z.infer<
  typeof ListCollectionThreadsOutputSchema
>;
export type RankedCollectionThreadDto = z.infer<
  typeof RankedCollectionThreadSchema
>;
export type ListRankedCollectionThreadsInput = z.infer<
  typeof ListRankedCollectionThreadsInputSchema
>;
export type ListRankedCollectionThreadsOutput = z.infer<
  typeof ListRankedCollectionThreadsOutputSchema
>;
export type InboxCustomerSummaryDto = z.infer<
  typeof InboxCustomerSummarySchema
>;
export type ListInboxCustomersInput = z.infer<
  typeof ListInboxCustomersInputSchema
>;
export type ListInboxCustomersOutput = z.infer<
  typeof ListInboxCustomersOutputSchema
>;
export type GetCollectionThreadInput = z.infer<
  typeof GetCollectionThreadInputSchema
>;
export type CollectionThreadReminderTaskDto = z.infer<
  typeof CollectionThreadReminderTaskSchema
>;
export type GetCollectionThreadReminderTaskInput = z.infer<
  typeof GetCollectionThreadReminderTaskInputSchema
>;
export type GetCollectionThreadReminderTaskOutput = z.infer<
  typeof GetCollectionThreadReminderTaskOutputSchema
>;
export type CreateCollectionThreadInput = z.infer<
  typeof CreateCollectionThreadInputSchema
>;
export type UpdateCollectionThreadInput = z.infer<
  typeof UpdateCollectionThreadInputSchema
>;
export type ResolveCollectionThreadInput = z.infer<
  typeof ResolveCollectionThreadInputSchema
>;
export type BulkResolveCollectionThreadsInput = z.infer<
  typeof BulkResolveCollectionThreadsInputSchema
>;
export type BulkResolveCollectionThreadsOutput = z.infer<
  typeof BulkResolveCollectionThreadsOutputSchema
>;
export type BulkAssignCollectionThreadsInput = z.infer<
  typeof BulkAssignCollectionThreadsInputSchema
>;
export type BulkAssignCollectionThreadsOutput = z.infer<
  typeof BulkAssignCollectionThreadsOutputSchema
>;
export type CollectionThreadPdfColumnId = z.infer<
  typeof CollectionThreadPdfColumnIdSchema
>;
export type DownloadCollectionThreadsPdfInput = z.infer<
  typeof DownloadCollectionThreadsPdfInputSchema
>;
export type DownloadCollectionThreadsPdfOutput = z.infer<
  typeof DownloadCollectionThreadsPdfOutputSchema
>;
export type ReopenCollectionThreadInput = z.infer<
  typeof ReopenCollectionThreadInputSchema
>;
export type SnoozeCollectionThreadInput = z.infer<
  typeof SnoozeCollectionThreadInputSchema
>;
export type SetCollectionThreadNextStepInput = z.infer<
  typeof SetCollectionThreadNextStepInputSchema
>;
export type AddCollectionThreadCommentInput = z.infer<
  typeof AddCollectionThreadCommentInputSchema
>;
export type CollectionThreadEvidencePaymentApplicationDto = z.infer<
  typeof CollectionThreadEvidencePaymentApplicationSchema
>;
export type CollectionThreadEvidenceReceiptDto = z.infer<
  typeof CollectionThreadEvidenceReceiptSchema
>;
export type CollectionThreadEvidenceRemittanceAdviceDto = z.infer<
  typeof CollectionThreadEvidenceRemittanceAdviceSchema
>;
export type CollectionThreadEvidenceInboundEmailDto = z.infer<
  typeof CollectionThreadEvidenceInboundEmailSchema
>;
export type CollectionThreadEvidenceDocumentDto = z.infer<
  typeof CollectionThreadEvidenceDocumentSchema
>;
export type CollectionThreadEvidenceShipmentDto = z.infer<
  typeof CollectionThreadEvidenceShipmentSchema
>;
export type CollectionThreadEvidenceInvoiceDto = z.infer<
  typeof CollectionThreadEvidenceInvoiceSchema
>;
export type CollectionThreadEvidenceSalesOrderDto = z.infer<
  typeof CollectionThreadEvidenceSalesOrderSchema
>;
export type CollectionThreadEvidenceCustomerPurchaseOrderDto = z.infer<
  typeof CollectionThreadEvidenceCustomerPurchaseOrderSchema
>;
export type CollectionThreadEvidenceApInvoiceDto = z.infer<
  typeof CollectionThreadEvidenceApInvoiceSchema
>;
export type ListCollectionThreadEvidenceInput = z.infer<
  typeof ListCollectionThreadEvidenceInputSchema
>;
export type ListCollectionThreadEvidenceOutput = z.infer<
  typeof ListCollectionThreadEvidenceOutputSchema
>;
export type ListCollectionThreadDocumentNamesInput = z.infer<
  typeof ListCollectionThreadDocumentNamesInputSchema
>;
export type ListCollectionThreadDocumentNamesOutput = z.infer<
  typeof ListCollectionThreadDocumentNamesOutputSchema
>;
export type FileCollectionThreadDocumentEvidenceInput = z.infer<
  typeof FileCollectionThreadDocumentEvidenceInputSchema
>;
export type FileCollectionThreadEmailFileEvidenceInput = z.infer<
  typeof FileCollectionThreadEmailFileEvidenceInputSchema
>;
export type FileCollectionThreadEmailFileEvidenceOutput = z.infer<
  typeof FileCollectionThreadEmailFileEvidenceOutputSchema
>;
export type LinkCollectionThreadApInvoiceEvidenceInput = z.infer<
  typeof LinkCollectionThreadApInvoiceEvidenceInputSchema
>;
export type LinkCollectionThreadApInvoiceEvidenceOutput = z.infer<
  typeof LinkCollectionThreadApInvoiceEvidenceOutputSchema
>;
export type UnlinkCollectionThreadApInvoiceEvidenceInput = z.infer<
  typeof UnlinkCollectionThreadApInvoiceEvidenceInputSchema
>;
export type UnlinkCollectionThreadApInvoiceEvidenceOutput = z.infer<
  typeof UnlinkCollectionThreadApInvoiceEvidenceOutputSchema
>;
export type RefreshCollectionThreadEvidenceInput = z.infer<
  typeof RefreshCollectionThreadEvidenceInputSchema
>;
export type RefreshCollectionThreadEvidenceOutput = z.infer<
  typeof RefreshCollectionThreadEvidenceOutputSchema
>;
export type RefreshCollectionThreadFromFamousInput = z.infer<
  typeof RefreshCollectionThreadFromFamousInputSchema
>;
export type RefreshCollectionThreadFromFamousOutput = z.infer<
  typeof RefreshCollectionThreadFromFamousOutputSchema
>;
export type CollectionThreadRefreshSkipReason = z.infer<
  typeof CollectionThreadRefreshSkipReasonSchema
>;
export type ReevaluateCollectionThreadInput = z.infer<
  typeof ReevaluateCollectionThreadInputSchema
>;
export type ReevaluateCollectionThreadOutput = z.infer<
  typeof ReevaluateCollectionThreadOutputSchema
>;
